/**
 * mSTEM Kit Manager Modal container (GEN-04 + SMH-04). Owns local
 * edit/create/connect/detail state (editingKit, isCreating, connectingKit,
 * detailKit); dispatches to kitsReducer; mirrors each write to localStorage
 * via writeKits so the data persists across page reloads.
 *
 * SMH-04: handleClickDetail opens the read-only "Thông tin chi tiết kit
 * SmartHome" view and pairs the selected kit with its mock firmware history
 * from src/lib/mstem-mock/firmwares.js.
 *
 * Persistence pattern (T-02-04-02): the helper persistAfterDispatch
 * computes the next list locally from the current Redux snapshot BEFORE
 * dispatching. This avoids races with redux-throttle (300ms coalesce
 * window applied to all dispatches at the store middleware layer).
 *
 * Mirrors RobotManagerModal container from Plan 02-03.
 */
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {defineMessages, injectIntl, intlShape} from 'react-intl';

import {closeKitManager} from '../reducers/modals';
import {addKit, updateKit, deleteKit, seedKits} from '../reducers/kits';
import {readKits, writeKits} from '../lib/mstem-storage';
import FIRMWARES from '../lib/mstem-mock/firmwares';

import KitManagerModalComponent from '../components/kit-manager-modal/kit-manager-modal.jsx';

const confirmMessages = defineMessages({
    confirmDelete: {
        defaultMessage: 'Xoá kit này khỏi danh sách?',
        description: 'Confirm dialog for deleting a kit',
        id: 'gen.kit.confirm.delete'
    }
});

class KitManagerModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleClickAdd',
            'handleClickEdit',
            'handleClickDelete',
            'handleClickConnect',
            'handleCloseConnect',
            'handleClickDetail',
            'handleCloseDetail',
            'handleSearch',
            'handleSubmitForm',
            'handleCancelForm',
            'handleRequestClose'
        ]);
        this.state = {
            editingKit: null,
            isCreating: false,
            connectingKit: null,
            detailKit: null,
            searchTerm: ''
        };
    }
    componentDidMount () {
        // Hydrate from localStorage on first mount (only if stored data exists).
        const stored = readKits();
        if (stored && Array.isArray(stored) && stored.length > 0) {
            this.props.onSeedKits(stored);
        }
    }
    persistAfterDispatch (mutator) {
        // Compute next list locally so writeKits fires with the expected
        // post-dispatch shape — independent of redux-throttle coalescing.
        const nextList = mutator(this.props.kits);
        writeKits(nextList);
    }
    handleClickAdd () {
        this.setState({isCreating: true, editingKit: null});
    }
    handleClickEdit (kit) {
        this.setState({editingKit: kit, isCreating: false});
    }
    handleClickDelete (id) {
        const msg = this.props.intl.formatMessage(confirmMessages.confirmDelete);
        // eslint-disable-next-line no-alert
        if (typeof window !== 'undefined' && window.confirm && !window.confirm(msg)) {
            return;
        }
        this.persistAfterDispatch(list => list.filter(k => k.id !== id));
        this.props.onDeleteKit(id);
    }
    handleSubmitForm (kit) {
        if (this.state.isCreating) {
            this.persistAfterDispatch(list => list.concat([kit]));
            this.props.onAddKit(kit);
        } else {
            this.persistAfterDispatch(list => list.map(k => (
                k.id === kit.id ? Object.assign({}, k, kit) : k
            )));
            this.props.onUpdateKit(kit);
        }
        this.setState({editingKit: null, isCreating: false});
    }
    handleCancelForm () {
        this.setState({editingKit: null, isCreating: false});
    }
    handleClickConnect (kit) {
        // MOCK-ONLY: open the reusable connection wizard for this kit.
        this.setState({connectingKit: kit});
    }
    handleCloseConnect () {
        this.setState({connectingKit: null});
    }
    handleClickDetail (kit) {
        // SMH-04: read the freshest copy from the store so an edit made just
        // before opening the detail view is reflected (the row object handed
        // in by the table may be a stale render-time snapshot).
        const fresh = this.props.kits.find(k => k.id === kit.id) || kit;
        this.setState({detailKit: fresh});
    }
    handleCloseDetail () {
        this.setState({detailKit: null});
    }
    handleSearch (e) {
        this.setState({searchTerm: e.target.value});
    }
    handleRequestClose () {
        this.setState({
            editingKit: null,
            isCreating: false,
            connectingKit: null,
            detailKit: null,
            searchTerm: ''
        });
        this.props.onClose();
    }
    render () {
        const {detailKit} = this.state;
        // MOCK-ONLY firmware registry lookup (SMH-04). Unknown kit id (e.g. a
        // kit the teacher created by hand) → empty history + the modal's
        // "Chưa có dữ liệu firmware" empty state.
        const detailFirmwares = detailKit ? (FIRMWARES[detailKit.id] || []) : [];
        return (
            <KitManagerModalComponent
                connectingKit={this.state.connectingKit}
                detailFirmwares={detailFirmwares}
                detailKit={detailKit}
                editingKit={this.state.editingKit}
                isCreating={this.state.isCreating}
                kits={this.props.kits}
                searchTerm={this.state.searchTerm}
                onCancelForm={this.handleCancelForm}
                onClickAdd={this.handleClickAdd}
                onClickConnect={this.handleClickConnect}
                onClickDelete={this.handleClickDelete}
                onClickDetail={this.handleClickDetail}
                onClickEdit={this.handleClickEdit}
                onCloseConnect={this.handleCloseConnect}
                onCloseDetail={this.handleCloseDetail}
                onRequestClose={this.handleRequestClose}
                onSearch={this.handleSearch}
                onSubmitForm={this.handleSubmitForm}
            />
        );
    }
}

KitManagerModal.propTypes = {
    intl: intlShape.isRequired,
    kits: PropTypes.arrayOf(PropTypes.object).isRequired, // eslint-disable-line react/forbid-prop-types
    onAddKit: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onDeleteKit: PropTypes.func.isRequired,
    onSeedKits: PropTypes.func.isRequired,
    onUpdateKit: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
    kits: state.scratchGui.kits.list
});

const mapDispatchToProps = dispatch => ({
    onClose: () => dispatch(closeKitManager()),
    onAddKit: kit => dispatch(addKit(kit)),
    onUpdateKit: kit => dispatch(updateKit(kit)),
    onDeleteKit: id => dispatch(deleteKit(id)),
    onSeedKits: list => dispatch(seedKits(list))
});

export default compose(
    injectIntl,
    connect(mapStateToProps, mapDispatchToProps)
)(KitManagerModal);
